import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ChevronRight,
  Pencil,
  MoreVertical,
  Trash2,
  Plus,
  Send,
  Loader2,
  Upload,
  Download,
  FileText,
  Clock,
} from "lucide-react";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import SelectMenu from "../../components/crm/SelectMenu";
import {
  PRIORITY_META,
  reminderLabel,
  deriveColumn,
  COLUMNS,
  initialsOf,
  avatarColor,
  fmtDueLong,
} from "./tasksShared";

const STATUS_PILL = {
  todo: "bg-amber-50 text-amber-700 ring-amber-200",
  inprogress: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  overdue: "bg-red-50 text-red-700 ring-red-200",
};

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { globalUser, globalData } = useAuth();
  const uid = globalUser?.uid;

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("comments");
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (!uid || !id) return;
    try {
      const snap = await getDoc(doc(db, "lawyers", uid, "tasks", id));
      if (!snap.exists()) {
        toast.error("Task not found");
        navigate("/portal/tasksevents");
        return;
      }
      setTask({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error("load task:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [uid, id]); // eslint-disable-line

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenuOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  const ref = useMemo(() => (uid && id ? doc(db, "lawyers", uid, "tasks", id) : null), [uid, id]);

  const nowISO = () => new Date().toISOString();
  const actor = () => globalData?.name || globalUser?.email || "You";
  const mkActivity = (text) => ({ id: `act-${Date.now()}`, text, byName: actor(), byUid: uid || "", at: nowISO() });
  const STATUS_LABEL = { todo: "To Do", inprogress: "In Progress", completed: "Completed" };

  // Update the doc and (optionally) append an activity-log entry in one write.
  const patch = async (data, activityText) => {
    if (!ref) return;
    const entry = activityText ? mkActivity(activityText) : null;
    const update = { ...data, updatedAt: nowISO() };
    if (entry) update.activity = arrayUnion(entry);
    await updateDoc(ref, update);
    setTask((t) => ({ ...t, ...data, ...(entry ? { activity: [...(t.activity || []), entry] } : {}) }));
  };

  const setStatus = (status) => patch({ status }, `Changed status to ${STATUS_LABEL[status] || status}`);

  const toggleChecklist = (itemId) => {
    const item = (task.checklist || []).find((c) => c.id === itemId);
    const next = (task.checklist || []).map((c) => (c.id === itemId ? { ...c, done: !c.done } : c));
    patch({ checklist: next }, item ? `${item.done ? "Unchecked" : "Checked"} "${item.text}"` : "Updated checklist");
  };

  const addChecklistItem = () => {
    const text = newItem.trim();
    if (!text) return;
    const item = { id: `${Date.now()}`, text, done: false };
    patch({ checklist: [...(task.checklist || []), item] }, `Added checklist item "${text}"`);
    setNewItem("");
  };

  const postComment = async () => {
    const text = comment.trim();
    if (!text || !ref) return;
    setPosting(true);
    try {
      const c = { id: `${Date.now()}`, text, authorName: actor(), authorUid: uid, at: nowISO() };
      const entry = mkActivity("Added a comment");
      await updateDoc(ref, { comments: arrayUnion(c), activity: arrayUnion(entry), updatedAt: nowISO() });
      setTask((t) => ({ ...t, comments: [...(t.comments || []), c], activity: [...(t.activity || []), entry] }));
      setComment("");
    } catch (e) {
      console.error("post comment:", e);
      toast.error("Failed to add comment");
    } finally {
      setPosting(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !ref) return;
    setUploading(true);
    try {
      for (const file of files) {
        const path = `tasks/${id}/${Date.now()}_${file.name}`;
        const sRef = storageRef(storage, path);
        await uploadBytes(sRef, file);
        const url = await getDownloadURL(sRef);
        const att = { id: `${Date.now()}`, name: file.name, url, path, size: file.size, type: file.type, byName: actor(), byUid: uid || "", at: nowISO() };
        const entry = mkActivity(`Uploaded ${file.name}`);
        await updateDoc(ref, { attachments: arrayUnion(att), activity: arrayUnion(entry), updatedAt: nowISO() });
        setTask((t) => ({ ...t, attachments: [...(t.attachments || []), att], activity: [...(t.activity || []), entry] }));
      }
      toast.success(files.length > 1 ? "Files uploaded" : "File uploaded");
    } catch (err) {
      console.error("upload:", err);
      toast.error("Failed to upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRemoveAttachment = async (att) => {
    if (!ref) return;
    try {
      const nextAtt = (task.attachments || []).filter((a) => a.id !== att.id);
      const entry = mkActivity(`Removed ${att.name}`);
      await updateDoc(ref, { attachments: nextAtt, activity: arrayUnion(entry), updatedAt: nowISO() });
      setTask((t) => ({ ...t, attachments: nextAtt, activity: [...(t.activity || []), entry] }));
      if (att.path) {
        try { await deleteObject(storageRef(storage, att.path)); } catch (_) { /* file may already be gone */ }
      }
    } catch (e) {
      console.error("remove attachment:", e);
      toast.error("Failed to remove");
    }
  };

  const handleDelete = async () => {
    if (!ref) return;
    if (!window.confirm("Delete this task? This can't be undone.")) return;
    try {
      // Cascade: remove any uploaded attachment files from Storage first, so
      // deleting a task doesn't leave orphaned blobs behind.
      await Promise.all(
        (task.attachments || [])
          .filter((a) => a.path)
          .map((a) => deleteObject(storageRef(storage, a.path)).catch(() => {}))
      );
      await deleteDoc(ref);
      toast.success("Task deleted");
      navigate("/portal/tasksevents");
    } catch (e) {
      console.error("delete task:", e);
      toast.error("Failed to delete task");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex h-[60vh] items-center justify-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }
  if (!task) return null;

  const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const col = COLUMNS.find((c) => c.key === deriveColumn({ ...task, type: "task" }));
  const checklist = task.checklist || [];
  const done = checklist.filter((c) => c.done).length;
  const pct = checklist.length ? Math.round((done / checklist.length) * 100) : 0;
  const comments = [...(task.comments || [])].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  const attachments = task.attachments || [];
  const activity = [...(task.activity || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const fmtBytes = (n) => {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1048576).toFixed(1)} MB`;
  };
  const fmtDateTime = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return isNaN(d) ? "—" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const infoRow = (label, value) => (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-right text-sm font-medium text-slate-800">{value}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/tasksevents" className="text-slate-500 hover:text-slate-800">Tasks &amp; Events</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Task</span>
        </div>

        {/* Header */}
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{task.title}</h1>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${pri.pill}`}>{pri.label}</span>
            </div>
            {task.linkedCaseName && (
              <p className="mt-1 text-sm text-slate-500">
                {task.linkedCaseName}{task.linkedCaseNo ? ` - ${task.linkedCaseNo}` : ""}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/portal/add-task?edit=${task.id}`)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <Pencil className="h-4 w-4" /> Edit Task
            </button>
            <div className="relative" ref={menuRef}>
              <button type="button" onClick={() => setMenuOpen((v) => !v)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 shadow-sm hover:bg-slate-50">
                <MoreVertical className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 z-20 mt-2 w-40 rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                  <button type="button" onClick={handleDelete} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Task Information */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800">Task Information</h3>
            <div className="mt-3 divide-y divide-slate-100">
              {infoRow("Assignee", (
                <span className="inline-flex items-center gap-2">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${avatarColor(task.assigneeName)}`}>
                    {initialsOf(task.assigneeName)}
                  </span>
                  {task.assigneeName || "—"}
                </span>
              ))}
              {infoRow("Priority", (
                <span className="inline-flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: pri.dot }} /> {pri.label}
                </span>
              ))}
              <div className="flex items-center justify-between gap-3 py-2">
                <span className="text-sm text-slate-500">Status</span>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_PILL[col?.key] || STATUS_PILL.todo}`}>
                    {col?.label || "To Do"}
                  </span>
                  <SelectMenu
                    value={task.status || "todo"}
                    onChange={(e) => setStatus(e.target.value)}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 focus:outline-none"
                  >
                    <option value="todo">To Do</option>
                    <option value="inprogress">In Progress</option>
                    <option value="completed">Completed</option>
                  </SelectMenu>
                </div>
              </div>
              {infoRow("Due Date", task.dueDate ? fmtDueLong(task.dueDate) : "—")}
              {infoRow("Reminder", reminderLabel(task.reminder, task.reminderAt))}
              {infoRow("Created On", fmtDateTime(task.createdAt))}
              {infoRow("Created By", task.createdBy || "—")}
            </div>
          </div>

          {/* Description + Checklist */}
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">Description</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">
                {task.description || "No description."}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800">Checklist</h3>
                <span className="text-xs text-slate-500">{done} of {checklist.length} completed</span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-2 flex-grow overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs font-medium text-slate-500">{pct}%</span>
              </div>
              <div className="mt-3 space-y-1.5">
                {checklist.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1 py-1.5 hover:bg-slate-50">
                    <input type="checkbox" checked={c.done} onChange={() => toggleChecklist(c.id)} className="h-4 w-4 accent-emerald-500" />
                    <span className={`text-sm ${c.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{c.text}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addChecklistItem()}
                  placeholder="Add item"
                  className="min-w-0 flex-grow rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-yellow-400 focus:outline-none"
                />
                <button type="button" onClick={addChecklistItem} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-slate-50">
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs: Comments / Attachments / Activity */}
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex gap-6 border-b border-slate-100">
            {["comments", "attachments", "activity"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`-mb-px border-b-2 pb-2.5 text-sm font-medium capitalize transition-colors ${
                  tab === t ? "border-yellow-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {t === "activity" ? "Activity Log" : t}
              </button>
            ))}
          </div>

          {tab === "comments" && (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && postComment()}
                  placeholder="Write a comment..."
                  className="min-w-0 flex-grow rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
                <button type="button" disabled={posting || !comment.trim()} onClick={postComment} className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-500 px-3.5 py-2 text-sm font-semibold text-gray-900 hover:bg-yellow-600 disabled:opacity-50">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {comments.length === 0 && <p className="text-sm text-slate-400">No comments yet.</p>}
                {comments.map((c) => (
                  <div key={c.id} className="flex gap-3">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarColor(c.authorName)}`}>
                      {initialsOf(c.authorName)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{c.authorName}</p>
                      <p className="text-sm text-slate-600">{c.text}</p>
                      <p className="mt-0.5 text-xs text-slate-400">{fmtDateTime(c.at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "attachments" && (
            <div className="mt-4">
              <input ref={fileRef} type="file" multiple className="hidden" onChange={handleUpload} />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading..." : "Upload file"}
              </button>
              <div className="mt-4 space-y-2">
                {attachments.length === 0 && <p className="text-sm text-slate-400">No attachments yet.</p>}
                {attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-grow">
                      <p className="truncate text-sm font-medium text-slate-800" title={a.name}>{a.name}</p>
                      <p className="text-xs text-slate-400">{fmtBytes(a.size)}{a.byName ? ` · ${a.byName}` : ""}</p>
                    </div>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600" title="Download">
                      <Download className="h-4 w-4" />
                    </a>
                    <button type="button" onClick={() => handleRemoveAttachment(a)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "activity" && (
            <div className="mt-4">
              {activity.length === 0 ? (
                <p className="text-sm text-slate-400">No activity yet.</p>
              ) : (
                <ol className="relative space-y-4 border-l border-slate-200 pl-5">
                  {activity.map((a) => (
                    <li key={a.id} className="relative">
                      <span className="absolute -left-[1.42rem] top-1 h-3 w-3 rounded-full bg-yellow-500 ring-4 ring-white" />
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold text-slate-800">{a.byName}</span> {a.text}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                        <Clock className="h-3 w-3" /> {fmtDateTime(a.at)}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
